import React, { useState, useMemo } from 'react';
import { Box, Chip, Popover, Badge, IconButton, Typography, Divider, Button } from '@mui/material';
import { Visibility, InfoOutline, FilterList, Close } from "@mui/icons-material";
import { AsyncMultiAutoComplete } from '../../../shared/components/AsyncMultiAutoComplete';

import { MetaDrivenTable, PaginationControls } from '../../../shared/components';
import { useDisplayField } from '../../../hooks/useDisplayField';
import { ColumnFilter, FilterOption } from './columnFilter';
import { useTaskNavigation } from '../hooks/useTaskNavigation';
import { useInfiniteStakeholders } from '../../projects/hooks/useStakeholders';

export const TableView: React.FC<{
    taskData: any[];
    openDrawer: (taskId: number) => void;
    pagination: { total: number; limit: number; page: number; };
    changeRowsPerPage: (rowsPerPage: number) => void;
    changePage: (page: number) => void;
    statusFilter: string[];
    priorityFilter: string[];
    actionTypeFilter: string[];
    ownerFilter: string[];
    onStatusFilterChange: (keys: string[]) => void;
    onPriorityFilterChange: (keys: string[]) => void;
    onActionTypeFilterChange: (keys: string[]) => void;
    onOwnerFilterChange: (keys: string[]) => void;
    projectId?: string;
    isContractorPerson?: boolean;
    onOpenContractorDrawer?: (objectType: string, objectId: number, projectId?: number | null, taskId?: number | null) => void;
}> = ({ taskData, openDrawer, pagination, changeRowsPerPage, changePage, statusFilter, priorityFilter, actionTypeFilter, ownerFilter, onStatusFilterChange, onPriorityFilterChange, onActionTypeFilterChange, onOwnerFilterChange, projectId, isContractorPerson, onOpenContractorDrawer }) => {

    const { handleNavigate } = useTaskNavigation();
    const { getFilterOptions } = useDisplayField([]);

    const [filterAnchorEl, setFilterAnchorEl] = useState<Record<string, HTMLElement | null>>({});
    const [localOwnerFilter, setLocalOwnerFilter] = useState<{ label: string; value: string }[]>([]);
    const [stakeholderSearch, setStakeholderSearch] = useState('');

   const {
    stakeholders,
    fetchNextPage: fetchNextStakeholdersPage,
    hasNextPage: hasStakeholdersNextPage,
    isFetchingNextPage: isFetchingStakeholdersNextPage,
    isLoading: isStakeholdersLoading,
  } = useInfiniteStakeholders({ search: stakeholderSearch });
 

    const predefinedOptions: Record<string, FilterOption[]> = useMemo(() => ({
        priority_display_text: getFilterOptions('TASK_PRIORITY'),
        status_display_text: getFilterOptions('TASK_STATUS'),
        object_type: getFilterOptions('TASK_ACTION'),
        owner: stakeholders.map((stakeholder: any) => ({
            key: String(stakeholder.user_id),
            display_text: stakeholder.full_name,
        }))
    }), [getFilterOptions, stakeholders]);
  

    const columnLabels: Record<string, string> = {
        priority_display_text: 'Priority',
        status_display_text: 'Status',
        object_type: 'Action Type',
        owner: 'Action Owner',
    };

    const columnSelectedKeys: Record<string, string[]> = {
        priority_display_text: priorityFilter,
        status_display_text: statusFilter,
        object_type: actionTypeFilter,
        owner: ownerFilter,
    };

    const createFilterHeader = (label: string, columnKey: string, onOpen?: () => void) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{label}</span>
            {predefinedOptions[columnKey] !== undefined && (
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        const target = e.currentTarget;
                        onOpen?.();
                        setFilterAnchorEl((prev) => ({ ...prev, [columnKey]: target }));
                    }}
                    sx={{ p: 0.5, ml: 'auto', pointerEvents: 'auto' }}
                >
                    <Badge
                        variant="dot"
                        invisible={!(columnSelectedKeys[columnKey]?.length > 0)}
                        color="error"
                        sx={{ '& .MuiBadge-dot': { width: 8, height: 8, minWidth: 8 } }}
                    >
                        <FilterList fontSize="small" />
                    </Badge>
                </IconButton>
            )}
        </Box>
    );

    const headers = [
        { alias_name: "task_name", label: createFilterHeader("Task Name", "task_name"), display_order: 0, is_show: true },
        { alias_name: "programme_name", label: createFilterHeader("Programme", "programme_name"), display_order: 1, is_show: true },
        { alias_name: "portfolio_name", label: createFilterHeader("Portfolio", "portfolio_name"), display_order: 2, is_show: true },
        { alias_name: "project_title", label: createFilterHeader("Project Title", "project_title"), display_order: 3, is_show: true },
        { alias_name: "phase_name", label: createFilterHeader("Current Phase", "phase_name"), display_order: 4, is_show: true},
        { alias_name: "action_type_display_text", label: createFilterHeader("Action Type", "object_type"), display_order: 5, is_show: true },
        { alias_name: "description", label: createFilterHeader("Description", "description"), display_order: 6, is_show: true },
        { alias_name: "category_display_text", label: createFilterHeader("Category", "category_display_text"), display_order: 7, is_show: true },
        { alias_name: "owner", label: createFilterHeader("Action Owner", "owner", () => {
            const opts = ownerFilter.map(id => {
                const match = stakeholders.find((s: any) => String(s.user_id) === id);
                return match ? { label: match.full_name, value: String(match.user_id) } : { label: id, value: id };
            });
            setLocalOwnerFilter(opts);
        }), display_order: 8, is_show: true },
        { alias_name: "due_date", label: createFilterHeader("Due Date", "due_date"), display_order: 9, is_show: true },
        { alias_name: "completed_at", label: createFilterHeader("Completed Date", "completed_at"), display_order: 10, is_show: true },
        { alias_name: "priority_display_text", label: createFilterHeader("Priority", "priority_display_text"), display_order: 14, is_show: true },
        { alias_name: "status_display_text", label: createFilterHeader("Status", "status_display_text"), display_order: 15, is_show: true },
    ];

    const metrics = {
        due_date: { type: 'date' },
        completed_at: { type: 'date' },
        target_date: { type: 'date' },
        tags_display_text: {
            type: 'component',
            element: ((row: { tags: String[] }) => (
                <>
                    {row.tags.length === 0 ? 'No Tags Assigned' : row.tags.map((tag, index) => (
                        <Chip key={index} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                    ))}
                </>
            ))
        }
    };

    const handleFilterApply = (columnKey: string, keys: string[]) => {
        if (columnKey === 'status_display_text') onStatusFilterChange(keys);
        else if (columnKey === 'priority_display_text') onPriorityFilterChange(keys);
        else if (columnKey === 'object_type') onActionTypeFilterChange(keys);
        else if (columnKey === 'owner') onOwnerFilterChange(keys);
        setFilterAnchorEl(prev => ({ ...prev, [columnKey]: null }));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            <MetaDrivenTable
                headers={headers}
                data={taskData}
                dataMetrics={metrics}
                actions={[
                    {
                        label: <InfoOutline />,
                        handleClick: (row: any) => {
                            // setDetails({ open: true, taskId: row.task_id });
                            openDrawer(row.task_id);
                        }
                    },
                    {
                        label: <Visibility />,
                        handleClick: (row: any) => handleNavigate(row, isContractorPerson, onOpenContractorDrawer),
                        isValidate: (row: any) => row.object_type !== 'task',
                        isHidden: (row: any) => row.object_type !== 'task'
                    },
                ]}
                sortDisabled
            />
            <PaginationControls
                total={pagination.total}
                rowsPerPage={pagination.limit}
                currentPage={pagination.page}
                handleRowsPerPage={changeRowsPerPage}
                handlePageChange={changePage}
            />

            {/* ColumnFilter rendered as child component via Popover */}
            {Object.entries(filterAnchorEl).map(([columnKey, anchorEl]) =>
                anchorEl ? (
                    <Popover
                        key={columnKey}
                        open
                        anchorEl={anchorEl}
                        onClose={() => setFilterAnchorEl(prev => ({ ...prev, [columnKey]: null }))}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                        {columnKey === 'owner' ? (
                            <Box sx={{ p: 2, minWidth: 300 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="subtitle2">Filter by Action Owner</Typography>
                                    <IconButton size="small" onClick={() => setFilterAnchorEl(prev => ({ ...prev, owner: null }))}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Divider sx={{ mb: 1 }} />
                                <AsyncMultiAutoComplete
                                    label="Action Owner"
                                    value={localOwnerFilter}
                                    onChange={(val) => setLocalOwnerFilter(val as { label: string; value: string }[])}
                                    options={stakeholders.map((s: any) => ({ label: s.full_name, value: String(s.user_id) }))}
                                    onSearch={(text) => setStakeholderSearch(text)}
                                    onScrollEnd={() => {
                                        if (hasStakeholdersNextPage && !isFetchingStakeholdersNextPage) {
                                            fetchNextStakeholdersPage();
                                        }
                                    }}
                                    loading={isStakeholdersLoading || isFetchingStakeholdersNextPage}
                                />
                                <Divider sx={{ mt: 1 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1.5 }}>
                                    <Button variant="outlined" size="small" onClick={() => {
                                        setLocalOwnerFilter([]);
                                        onOwnerFilterChange([]);
                                        setFilterAnchorEl(prev => ({ ...prev, owner: null }));
                                    }}>
                                        Reset
                                    </Button>
                                    <Button variant="contained" size="small" color="primary" onClick={() => {
                                        onOwnerFilterChange(localOwnerFilter.map(o => String(o.value)));
                                        setFilterAnchorEl(prev => ({ ...prev, owner: null }));
                                    }}>
                                        Apply
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <ColumnFilter
                                columnLabel={columnLabels[columnKey] ?? columnKey.replace(/_/g, ' ')}
                                selected={columnSelectedKeys[columnKey] || []}
                                options={predefinedOptions[columnKey] || []}
                                onApply={(keys) => handleFilterApply(columnKey, keys)}
                                onClose={() => setFilterAnchorEl(prev => ({ ...prev, [columnKey]: null }))}
                            />
                        )}
                    </Popover>
                ) : null
            )}
        </Box>
    );
};